
import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/shared/data/data.service';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-order-summery',
  templateUrl: './order-summery.component.html',
  styleUrl: './order-summery.component.scss'
})
export class OrderSummeryComponent implements OnInit, OnDestroy {

  orders: any[] = [];
  customerName = localStorage.getItem('customer_name');

  pollSubscription!: Subscription;
  timerSubscription!: Subscription;

  cancellingOrderId: number | null = null;
  currentTime: number = Date.now();

  private readonly CANCEL_WINDOW_MS = 5 * 60 * 1000;
  private readonly LA_TIMEZONE = 'America/Los_Angeles';

  constructor(
    private http: HttpClient,
    private data: DataService
  ) {}

  ngOnInit(): void {
    this.loadOrders();

    // Refresh orders every 15 seconds
    this.pollSubscription = interval(15000).subscribe(() => {
      this.loadOrders();
    });

    // Refresh countdown every second
    this.timerSubscription = interval(1000).subscribe(() => {
      this.currentTime = Date.now();
    });
  }

  ngOnDestroy(): void {
    this.pollSubscription?.unsubscribe();
    this.timerSubscription?.unsubscribe();
  }

  // Load customer orders
  loadOrders(): void {
    const payload = {
      customer_id: localStorage.getItem('customer_id')
    };

    this.http.post<any[]>(
      this.data.base_url + 'get_my_orders/',
      payload
    ).subscribe({
      next: (res) => {
        this.orders = (res || []).map(order => ({
          ...order,
          open: false
        }));
      },
      error: (error) => {
        console.error('Load orders error:', error);
      }
    });
  }

  // Display order date in Los Angeles timezone
  formatOrderDate(date: string): string {
    if (!date) {
      return '';
    }

    const orderDate = new Date(date);

    if (isNaN(orderDate.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-US', {
      timeZone: this.LA_TIMEZONE,
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(orderDate);
  }

  // Check whether order can be cancelled within 5 minutes
  canCancelOrder(order: any): boolean {
    if (!order.ordered_at) {
      return false;
    }

    const orderTime = Date.parse(order.ordered_at);

    if (isNaN(orderTime)) {
      return false;
    }

    const elapsed = this.currentTime - orderTime;

    const status = String(order.status)
      .trim()
      .toLowerCase();

    return (
      elapsed >= 0 &&
      elapsed < this.CANCEL_WINDOW_MS &&
      status !== 'cancelled' &&
      status !== 'canceled' &&
      status !== '7'
    );
  }

  // Get remaining cancellation time
  getRemainingTime(order: any): string {
    if (!order.ordered_at) {
      return '00:00';
    }

    const orderTime = Date.parse(order.ordered_at);

    if (isNaN(orderTime)) {
      return '00:00';
    }

    const elapsed = this.currentTime - orderTime;

    const remaining = Math.max(
      0,
      this.CANCEL_WINDOW_MS - elapsed
    );

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  // Cancel order
  cancelOrder(orderId: number): void {

    if (this.cancellingOrderId !== null) {
      return;
    }

    this.cancellingOrderId = orderId;

    const payload = {
      order_id: orderId,
      status_id: 7
    };

    this.http.post(
      this.data.base_url + 'update_order_status/',
      payload
    ).subscribe({
      next: (res: any) => {

        if (res.status === 1) {
          this.loadOrders();
        } else {
          alert('Failed to cancel order: ' + res.message);
        }

        this.cancellingOrderId = null;
      },
      error: (error) => {
        console.error('Cancel order error:', error);

        alert('Unable to cancel order. Please try again.');

        this.cancellingOrderId = null;
      }
    });
  }
}
